import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { prepareAuthRedirect } from '@/utils/authRedirect';

const isVitestMode = import.meta.env.MODE === 'test';
const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ||
  (isVitestMode ? 'https://example.supabase.co' : '');
const SUPABASE_PUBLISHABLE_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ||
  (isVitestMode ? 'ci-placeholder-anon-key' : '');
const browserLocalStorage = (() => {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
})();

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: browserLocalStorage,
    persistSession: true,
    autoRefreshToken: true
  }
});

// Production: use session token
export const getAuthHeaders = async () => {
  const { data } = await supabase.auth.getSession();
  const session = data?.session;
  if (session?.access_token) {
    return {
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': SUPABASE_PUBLISHABLE_KEY
    };
  }

  if (import.meta.env.DEV) {
    // Fall back to the service role only when there is no signed-in user session.
    const serviceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;
    if (serviceRoleKey) {
      return {
        'Authorization': `Bearer ${serviceRoleKey}`,
        'apikey': serviceRoleKey
      };
    }
  }

  return {
    'apikey': SUPABASE_PUBLISHABLE_KEY
  };
};

/**
 * Check if an error indicates the user's session has expired or is invalid.
 * Use this to detect when API calls fail due to auth issues.
 */
export const isAuthError = (error: unknown): boolean => {
  if (!error) return false;

  const errorMessage = error instanceof Error ? error.message : String(error);
  const lowerMessage = errorMessage.toLowerCase();

  // Check for common auth-related error patterns
  const authPatterns = [
    'unauthorized',
    '401',
    'jwt expired',
    'invalid token',
    'no session',
    'session expired',
    'not authenticated',
    'authentication required',
    'forbidden',
    '403',
  ];

  return authPatterns.some(pattern => lowerMessage.includes(pattern));
};

/**
 * Handle auth errors by redirecting to home page.
 * Call this in catch blocks when Edge Function calls fail.
 */
export const handleAuthError = (error: unknown): boolean => {
  if (isAuthError(error)) {
    console.log('[handleAuthError] Auth error detected, clearing session and redirecting');

    // Clear storage to be extra sure
    try {
      browserLocalStorage?.removeItem('supabase.auth.token');
    } catch {
      // Ignore storage failures.
    }
    prepareAuthRedirect('expired');

    // Attempt sign out but redirect anyway
    supabase.auth.signOut().finally(() => {
      window.location.replace('/login');
    });

    // Set a short timeout to force redirect if signOut hangs
    setTimeout(() => {
      window.location.replace('/login');
    }, 1000);

    return true;
  }
  return false;
};
